import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { t } from 'i18next';
import {
  type DefaultValues,
  type Path,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from '@/components/Form';
import { SlideIn } from '@/components/SlideIn';

type StatusFilterItem<T> = {
  name: T;
  text: string;
};

// Define a generic type for the form values
type StatusItemsObj<T extends string> = Record<T, boolean>;

type StatusFilterComponentProps<T extends string> = {
  statusFilterItems: Array<StatusFilterItem<T>>;
  initialValues?: StatusItemsObj<T>;
  onSubmit: (data: StatusItemsObj<T>) => void;
  onclose: () => void;
  onClearStatusFilter?: () => void;
  onOpenExplanation?: () => void;
};

export const StatusFilterComponent = <T extends string>({
  statusFilterItems,
  initialValues,
  onSubmit,
  onclose,
  onClearStatusFilter,
  onOpenExplanation,
}: StatusFilterComponentProps<T>) => {
  type FormInputs = StatusItemsObj<T>;

  const statusesSettingsModal = useDisclosure();
  const form = useForm<FormInputs>({
    mode: 'onBlur',
    defaultValues: initialValues as
      | DefaultValues<StatusItemsObj<T>>
      | undefined,
  });

  const onFormSubmit: SubmitHandler<FormInputs> = async (data) => {
    onSubmit({ ...data });
    onclose();
  };

  return (
    <Flex flexDir="column" p={3}>
      <HStack justify="space-between" mb={5}>
        <Text size="sm" fontWeight="medium" color="gray.700">
          {t('common:detail.status')}
        </Text>
        <Button
          variant="link"
          colorScheme="primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpenExplanation?.() ?? statusesSettingsModal.onToggle();
          }}
        >
          {t('common:explanation')}
        </Button>

        <StatusesExplanationModal
          isOpen={statusesSettingsModal.isOpen}
          onClose={statusesSettingsModal.onClose}
        />

        {onClearStatusFilter && (
          <Button
            size="sm"
            color="brand.500"
            onClick={(e) => {
              e.stopPropagation();
              onClearStatusFilter();
            }}
          >
            {t('common:actions.clear')}
          </Button>
        )}
      </HStack>

      <Form {...form} onSubmit={onFormSubmit}>
        <Flex flexDir="column" maxH="400px" overflowY="auto" rowGap={4}>
          {statusFilterItems.map((item) => {
            return (
              <FormField
                key={item.name}
                name={item.name as unknown as Path<StatusItemsObj<T>>}
                control={form.control}
                type="checkbox"
                size="lg"
              >
                <Text color="gray.900" size="sm">
                  {item.text}
                </Text>
              </FormField>
            );
          })}
        </Flex>

        <Button type="submit" mt={4} variant="@primary" size="sm" w="full">
          {t('common:actions.applyFilter')}
        </Button>
      </Form>
    </Flex>
  );
};

type StatusesExplanationModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const StatusesExplanationModal: React.FC<
  StatusesExplanationModalProps
> = (props) => {
  const { t } = useTranslation(['interview', 'components']);
  const statusExplanations = [
    {
      id: 0,
      title: t('interview:statuses.statusAdded'),
      description: t('interview:statuses.statusAddedDesc'),
      color: 'gray.50',
      textColor: 'gray.700',
    },
    {
      id: 1,
      title: t('interview:statuses.statusInvited'),
      description: t('interview:statuses.statusInvitedDesc'),
      color: 'info.50',
      textColor: 'info.700',
    },
    {
      id: 2,
      title: t('interview:statuses.statusNotCompleted'),
      description: t('interview:statuses.statusNotCompletedDesc'),
      color: 'error.50',
      textColor: 'error.700',
    },
    {
      id: 3,
      title: t('interview:statuses.statusCompleted'),
      description: t('interview:statuses.statusCompletedDesc'),
      color: 'info.50',
      textColor: 'info.700',
    },
    {
      id: 4,
      title: t('interview:statuses.statusPaused'),
      description: t('interview:statuses.statusPausedDesc'),
      color: 'warning.50',
      textColor: 'warning.700',
    },
    {
      id: 5,
      title: t('interview:statuses.statusPositive'),
      description: t('interview:statuses.statusPositiveDesc'),
      color: 'success.50',
      textColor: 'success.700',
    },
    {
      id: 6,
      title: t('interview:statuses.statusNegative'),
      description: t('interview:statuses.statusNegativeDesc'),
      color: 'error.50',
      textColor: 'error.700',
    },
    {
      id: 7,
      title: t('interview:statuses.statusPending'),
      description: t('interview:statuses.statusPendingDesc'),
      color: 'warning.50',
      textColor: 'warning.700',
    },
    {
      id: 8,
      title: t('interview:statuses.statusStarted'),
      description: t('interview:statuses.statusStartedDesc'),
      color: 'primary.50',
      textColor: 'primary.700',
    },
    {
      id: 9,
      title: t('interview:statuses.statusEvaluated'),
      description: t('interview:statuses.statusEvaluatedDesc'),
      color: 'success.50',
      textColor: 'success.700',
    },
    {
      id: 10,
      title: t('interview:statuses.statusActionRequired'),
      description: t('interview:statuses.statusActionRequiredDesc'),
      color: 'gray.50',
      textColor: 'gray.700',
    },
    {
      id: 11,
      title: t('interview:statuses.statusNotInterested'),
      description: t('interview:statuses.statusNotInterestedDesc'),
      color: 'gray.50',
      textColor: 'gray.700',
    },
    {
      id: 12,
      title: t('interview:statuses.statusInternalProcess'),
      description: t('interview:statuses.statusInternalProcessDesc'),
      color: 'info.50',
      textColor: 'info.700',
    },
    {
      id: 13,
      title: t('interview:statuses.statusPreliminaryCompleted'),
      description: t('interview:statuses.statusPreliminaryCompletedDesc'),
      color: 'info.50',
      textColor: 'info.700',
    },
    {
      id: 14,
      title: t('interview:statuses.statusOfferAccepted'),
      description: t('interview:statuses.statusOfferAcceptedDesc'),
      color: 'success.50',
      textColor: 'success.700',
    },
    {
      id: 15,
      title: t('interview:statuses.statusOfferSent'),
      description: t('interview:statuses.statusOfferSentDesc'),
      color: 'info.50',
      textColor: 'info.700',
    },
    {
      id: 16,
      title: t('interview:statuses.statusReferenceCheck'),
      description: t('interview:statuses.statusReferenceCheckDesc'),
      color: 'gray.50',
      textColor: 'gray.700',
    },
    {
      id: 17,
      title: t('interview:statuses.statusWithdrawn'),
      description: t('interview:statuses.statusWithdrawnDesc'),
      color: 'warning.50',
      textColor: 'warning.700',
    },
    {
      id: 18,
      title: t('interview:statuses.statusOfferRejected'),
      description: t('interview:statuses.statusOfferRejectedDesc'),
      color: 'error.50',
      textColor: 'error.700',
    },
  ];

  return (
    <Modal
      motionPreset="slideInRight"
      isOpen={props.isOpen}
      onClose={props.onClose}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="xl"
        alignItems="center"
        pos="absolute"
        top="16px"
        right="24px"
        bottom="16px"
        m={0}
        maxHeight="none"
      >
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
          mt={2}
        />
        <ModalHeader
          w="full"
          justifyItems="start"
          borderBottom="1px solid"
          borderBottomColor="gray.50"
        >
          <Text fontWeight="medium">
            {t('components:board.statusExplanation')}
          </Text>
        </ModalHeader>

        <ModalBody w="full" pb={2}>
          <SlideIn>
            <Stack spacing={4}>
              {statusExplanations.map((status) => (
                <Stack
                  key={status.id}
                  borderWidth={1}
                  borderColor="gray.50"
                  rounded="xl"
                  p={3}
                >
                  <Text
                    w="fit-content"
                    rounded="full"
                    px={3}
                    py={1}
                    bg={status.color}
                    color={status.textColor}
                    fontWeight="500"
                  >
                    {status.title}
                  </Text>
                  <Text>{status.description}</Text>
                </Stack>
              ))}
            </Stack>
          </SlideIn>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
